import GeneralPageLayout from '@common/src/components/GeneralPageLayout';
import { Box, Collapse, HStack, Text, Button } from '@wegroup/design-system';
import { Phone, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { CategoryTabs } from '../components/market/CategoryTabs';
import { ProductList } from '../components/market/ProductList';
import { SearchBar } from '../components/market/SearchBar';
import { TagFilter } from '../components/market/TagFilter';
import { sections, getTagsForCategory } from '../data/mockdata';
import { ProductCategory } from '../types/yellowHiveTypes';
import '../yellowHiveCss.css';

const YellowHiveMarket: React.FC = () => {
  const [category, setCategory] = useState<ProductCategory>('Particulier');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const handleTagToggle = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  return (
    <GeneralPageLayout>
      <div className="container">
        <Text textStyle="h1Headline" align="center">
          Marktplaats
        </Text>
        <Text
          textStyle="normalBody"
          align="center"
          maxW="4xl"
          mx="auto"
          mb="6"
          mt="3"
          color="gray.500"
        >
          Snel en eenvoudig producten vinden door te zoeken op sector, product
          of beschrijving. Klik op een product om direct naar de bijbehorende
          pagina te gaan.
        </Text>

        <CategoryTabs
          activeCategory={category}
          onCategoryChange={setCategory}
        />

        <SearchBar value={searchTerm} onChange={setSearchTerm} />

        <Box
          mt="4"
          mb="4"
          p="4"
          bg="rgba(0, 0, 0, 0.03)"
          border="1px solid"
          borderColor="gray.100"
          borderRadius="8px"
        >
          <ContactInline category={category} />
        </Box>

        <Collapse in={true} animateOpacity>
          <TagFilter
            tags={getTagsForCategory(category)}
            selectedTags={selectedTags}
            onTagToggle={handleTagToggle}
          />
        </Collapse>

        <ProductList
          sections={sections}
          category={category}
          searchTerm={searchTerm}
          selectedTags={selectedTags}
        />
      </div>
    </GeneralPageLayout>
  );
};

export default YellowHiveMarket;

const ContactInline: React.FC<{ category: ProductCategory }> = ({
  category,
}) => {
  const phone = category === 'Bedrijfsmatig' ? '085 083 1029' : '085 083 1027';
  const email =
    category === 'Bedrijfsmatig'
      ? 'acceptatiezakelijk@yinco.nl'
      : 'acceptatieparticulier@yinco.nl';

  const [copied, setCopied] = React.useState<'phone' | 'email' | null>(null);

  const handleCopy = async (value: string, type: 'phone' | 'email') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(type);
      setTimeout(() => setCopied(null), 1500);
    } catch (_e) {
      // ignore
    }
  };

  return (
    <HStack spacing="6" align="center" justify="space-between" w="100%">
      <Text textStyle="smallHeadline" mr="2">
        Vragen? Neem contact op —{' '}
        {category === 'Bedrijfsmatig' ? 'Zakelijk' : 'Particulier'}
      </Text>
      <HStack spacing="2">
        <Phone size={16} />
        <Text
          as="a"
          href={`tel:${phone.replace(/\s/g, '')}`}
          fontWeight="semibold"
          color="yellow.600"
        >
          {phone}
        </Text>
        <Button
          size="xs"
          variant="white"
          onClick={() => handleCopy(phone, 'phone')}
        >
          {copied === 'phone' ? 'Gekopieerd' : 'Kopieer'}
        </Button>
      </HStack>
      <HStack spacing="2">
        <Mail size={16} />
        <Text
          as="a"
          href={`mailto:${email}`}
          fontWeight="semibold"
          color="yellow.600"
        >
          {email}
        </Text>
        <Button
          size="xs"
          variant="white"
          onClick={() => handleCopy(email, 'email')}
        >
          {copied === 'email' ? 'Gekopieerd' : 'Kopieer'}
        </Button>
      </HStack>
    </HStack>
  );
};
